import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, BookOpen } from 'lucide-react';

interface ModuleMeta {
  title: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedMinutes: number;
  category: string;
  relatedFindingCategories: string[];
  content: string;
}

const DIFFICULTY_STYLES: Record<string, { label: string; className: string }> = {
  beginner: { label: 'Beginner', className: 'bg-green-100 text-green-800' },
  intermediate: { label: 'Intermediate', className: 'bg-yellow-100 text-yellow-800' },
  advanced: { label: 'Advanced', className: 'bg-red-100 text-red-800' },
};

// Module metadata + content for MVP
const MODULES: Record<string, ModuleMeta> = {
  'https-basics': {
    title: 'HTTPS & Encryption Basics',
    difficulty: 'beginner',
    estimatedMinutes: 5,
    category: 'ssl-tls',
    relatedFindingCategories: ['ssl-tls'],
    content: `## What is HTTPS?

HTTPS (Hypertext Transfer Protocol Secure) is like sending a letter in a sealed envelope instead of a postcard. When your site uses HTTPS, all data between your visitors and your server is encrypted.

## Why Does It Matter?

Without HTTPS:
- **Passwords are visible**: Anyone on the same WiFi can see login credentials
- **Personal data is exposed**: Form submissions travel in plain text
- **Browsers show warnings**: Chrome and Firefox mark HTTP sites as "Not Secure"
- **SEO suffers**: Google ranks HTTPS sites higher

## How to Get HTTPS

The easiest way is **Let's Encrypt** -- it's free and automatic:

1. If you're on a hosting platform (Vercel, Netlify, AWS), HTTPS is usually automatic
2. For your own server, install Certbot
3. Certbot will automatically renew your certificate

## Quick Checklist

- Your site loads on https:// (not just http://)
- HTTP automatically redirects to HTTPS
- Your certificate is valid and not expired
- You're using TLS 1.2 or 1.3 (not older versions)`,
  },
  'security-headers-101': {
    title: 'Security Headers 101',
    difficulty: 'beginner',
    estimatedMinutes: 8,
    category: 'security-headers',
    relatedFindingCategories: ['security-headers'],
    content: `## What Are Security Headers?

Security headers are instructions you send with every page response that tell browsers how to behave securely.

## The Essential Headers

### 1. Strict-Transport-Security (HSTS)
**What it does**: Forces browsers to always use HTTPS.

### 2. Content-Security-Policy (CSP)
**What it does**: Controls which scripts, styles, and resources can load on your page.

### 3. X-Frame-Options
**What it does**: Prevents your site from being embedded in frames on other sites.

### 4. X-Content-Type-Options
**What it does**: Stops browsers from guessing file types.`,
  },
  'xss-explained': {
    title: 'Cross-Site Scripting (XSS) Explained',
    difficulty: 'intermediate',
    estimatedMinutes: 10,
    category: 'xss',
    relatedFindingCategories: ['xss'],
    content: `## What is XSS?

Cross-Site Scripting (XSS) is an attack where malicious scripts are injected into trusted websites.

## Types of XSS

### Reflected XSS
The malicious script comes from the current HTTP request.

### Stored XSS
The malicious script is permanently stored on the target server.

### DOM-based XSS
The vulnerability exists in client-side code rather than server-side code.

## How to Prevent XSS

1. **Escape output**: Always encode user input before displaying it in HTML
2. **Use Content-Security-Policy**: Restrict which scripts can execute
3. **Validate input**: Check that input matches expected patterns
4. **Use modern frameworks**: React, Vue, and Angular escape output by default
5. **Set HttpOnly cookies**: Prevent JavaScript from accessing sensitive cookies`,
  },
};

function renderMarkdown(content: string) {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let key = 0;

  function flushList() {
    if (listItems.length > 0 && listType) {
      const Tag = listType;
      const className = listType === 'ul' ? 'list-disc' : 'list-decimal';
      elements.push(
        <Tag key={key++} className={`${className} pl-6 space-y-1 text-gray-700 dark:text-foreground`}>
          {listItems.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </Tag>
      );
      listItems = [];
      listType = null;
    }
  }

  function renderInline(text: string): React.ReactNode {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      const codeParts = part.split(/(`[^`]+`)/g);
      if (codeParts.length > 1) {
        return codeParts.map((cp, j) => {
          if (cp.startsWith('`') && cp.endsWith('`')) {
            return (
              <code key={`${i}-${j}`} className="bg-gray-100 dark:bg-muted px-1 py-0.5 rounded text-sm font-mono">
                {cp.slice(1, -1)}
              </code>
            );
          }
          return cp;
        });
      }
      return part;
    });
  }

  for (const line of lines) {
    const trimmed = line.trimEnd();

    if (trimmed === '') {
      flushList();
      continue;
    }

    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h4 key={key++} className="text-base font-semibold text-gray-900 dark:text-foreground mt-6 mb-2">
          {renderInline(trimmed.slice(4))}
        </h4>
      );
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h3 key={key++} className="text-lg font-semibold text-gray-900 dark:text-foreground mt-8 mb-3">
          {renderInline(trimmed.slice(3))}
        </h3>
      );
      continue;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push(
        <h2 key={key++} className="text-xl font-bold text-gray-900 dark:text-foreground mt-8 mb-4">
          {renderInline(trimmed.slice(2))}
        </h2>
      );
      continue;
    }

    if (trimmed.startsWith('- ')) {
      if (listType !== 'ul') {
        flushList();
        listType = 'ul';
      }
      listItems.push(trimmed.slice(2));
      continue;
    }

    const olMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
    if (olMatch) {
      if (listType !== 'ol') {
        flushList();
        listType = 'ol';
      }
      listItems.push(olMatch[2]);
      continue;
    }

    flushList();
    elements.push(
      <p key={key++} className="text-gray-700 dark:text-foreground leading-relaxed">
        {renderInline(trimmed)}
      </p>
    );
  }

  flushList();
  return elements;
}

export function LessonPage() {
  const { moduleId } = useParams();
  const [completed, setCompleted] = useState(false);

  const module = moduleId ? MODULES[moduleId] : undefined;

  if (!module) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">Module Not Found</h2>
          <p className="text-muted-foreground dark:text-muted-foreground mt-1">
            This module doesn't exist or content is coming soon.
          </p>
        </div>
      </div>
    );
  }

  const diffStyle = DIFFICULTY_STYLES[module.difficulty];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${diffStyle.className}`}
          >
            {diffStyle.label}
          </span>
          <span className="text-xs text-muted-foreground dark:text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {module.estimatedMinutes} min read
          </span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">{module.title}</h1>
      </div>

      <div className="space-y-4">
        {renderMarkdown(module.content)}
      </div>

      <div className="border-t border-gray-200 dark:border-border pt-6 mt-8 flex items-center justify-between">
        <div>
          {module.relatedFindingCategories.length > 0 && (
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">
              Related to findings in:{' '}
              {module.relatedFindingCategories.map((cat) => (
                <Badge key={cat} variant="outline" className="ml-1 text-xs">
                  {cat}
                </Badge>
              ))}
            </p>
          )}
        </div>
        <Button
          onClick={() => setCompleted(true)}
          disabled={completed}
          className={completed ? 'bg-green-600 hover:bg-green-600' : ''}
        >
          {completed ? (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Completed
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark as Complete
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
